/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { tagName } from '@ember-decorators/component';
import { inject as service } from '@ember/service';
import { lt, gt } from '@ember/object/computed';
import Component from '@ember/component';
import clamp from 'cio/utils/clamp';
import { set, action } from '@ember/object';
import { computed } from '@ember/object';
import { MAX_RANDOM_COHORT_ALLOCATION, RANDOM_COHORT_ALLOCATION_DIVISOR } from 'cio/utils/visual-workflow-builder';
import { formatRandomCohortPathValue } from 'cio/utils/visual-workflow-builder/branching';

const MIN_PATHS_COUNT = 2;

@tagName('')
export default class Editor extends Component {
  @service api;
  @service apiErrors;
  @service store;

  actionAbstract = null;
  isSaving = false;
  onSelect() {}
  onDeselect() {}

  @computed('actionAbstract.totalCohortAllocations', function () {
    return formatRandomCohortPathValue(
      Math.abs(MAX_RANDOM_COHORT_ALLOCATION - this.actionAbstract.totalCohortAllocations),
      true,
    );
  })
  _totalAllocationsDifference;

  @gt('actionAbstract.totalCohortAllocations', MAX_RANDOM_COHORT_ALLOCATION) _totalAllocationsOver;

  @lt('actionAbstract.totalCohortAllocations', MAX_RANDOM_COHORT_ALLOCATION) _totalAllocationsUnder;

  _setCohortValue(cohortAbstract, index, value, inputElement) {
    // The numbers from inputs range from 0 to 100, but we save cohort values
    // from 0 to 1000, so we need to multiply them.
    // We also apply Math.round to make sure the final number has no decimals,
    // since the backend only accepts integers.
    let adjustedValue = Math.round(value * RANDOM_COHORT_ALLOCATION_DIVISOR);
    let targetValue = clamp(adjustedValue, MAX_RANDOM_COHORT_ALLOCATION, 0);
    let { cohortAbstracts } = this.actionAbstract;

    if (this.actionAbstract.isDistributedEvenly) {
      let { evenDistributionValue } = this.actionAbstract;
      // if we're currently in "even distribution mode" (meaning that all
      // cohorts have a value of 1 or 0), we need to reset the cohort values to
      // what we're actually showing in the UI
      this.actionAbstract.cohortAbstracts
        .filter((cohortAbstract) => cohortAbstract.value === 1)
        .forEach((cohortAbstract) => set(cohortAbstract, 'value', evenDistributionValue));
    }
    set(cohortAbstract, 'value', targetValue);

    // If the input DOM element was provided, we update its value here manually.
    // Imagine this scenario: the current value is 33.3%, translating to a cohort
    // value of 333. If someone changes that to 33.34, it'll still translate to
    // 333 due to the rounding we use, but the input will still show 33.34 -
    // this is because the computed value that's used for the input value
    // doesn't update, because 333 stays the same.
    // We only allow 1 decimal point precision, so leaving 33.34 in the input is
    // misleading.
    if (inputElement) {
      inputElement.value = formatRandomCohortPathValue(targetValue);
    }

    if (cohortAbstracts.length === 2) {
      set(cohortAbstracts[(index + 1) % 2], 'value', MAX_RANDOM_COHORT_ALLOCATION - targetValue);
    }
  }

  @computed('actionAbstract.cohortAbstracts.length', function () {
    return this.actionAbstract.cohortAbstracts.length === MIN_PATHS_COUNT;
  })
  _hasMinimumPathCount;

  @computed('_hasMinimumPathCount', function () {
    if (this._hasMinimumPathCount) {
      return 'You need to have at least 2 paths';
    }

    return 'Only empty paths can be removed';
  })
  _deletePathNotice;

  @action
  addPath() {
    this.actionAbstract.addPath();
  }

  @action
  removePath(cohortAbstract) {
    this.actionAbstract.removePath(cohortAbstract);
  }

  @action
  distributeEvenly() {
    this.actionAbstract.cohortAbstracts.forEach((cohortAbstract) => set(cohortAbstract, 'value', 1));
  }

  @action
  onCohortSliderChange(cohortAbstract, index, value) {
    this._setCohortValue(cohortAbstract, index, value);
  }

  @action
  onCohortInput(cohortAbstract, index, event) {
    this._setCohortValue(cohortAbstract, index, event.target.value, event.target);
  }
}
